import { For, Show } from "solid-js";
import { checkIntegrity } from "./governStatus";
import type { GovernCheck } from "./governTypes";

const RESULT_LABELS: Record<GovernCheck["result"], string> = {
  not_checked: "Not checked",
  pass: "Pass",
  fail: "Fail",
  not_applicable: "Not applicable",
};

export function GovernChecks(props: {
  checks: GovernCheck[];
  editable: boolean;
  readOnlyMessage: string | null;
  onEdit: (checkId: string, trigger: HTMLElement) => void;
}) {
  return (
    <section class="govern-plan-section govern-checks-section">
      <header>
        <div>
          <span class="signal-mini-kicker">Brief acceptance criteria</span>
          <h3>Verification checks</h3>
        </div>
        <small>{props.checks.length} required</small>
      </header>
      <Show when={props.readOnlyMessage}>
        {(message) => (
          <p class="govern-checks-note" role="status">
            {message()}
          </p>
        )}
      </Show>
      <Show
        when={props.checks.length > 0}
        fallback={
          <div class="govern-limited-state">
            This Brief has no acceptance criteria to verify.
          </div>
        }
      >
        <div class="govern-check-list">
          <For each={props.checks}>
            {(check, index) => (
              <GovernCheckRow
                check={check}
                index={index()}
                editable={props.editable}
                onEdit={props.onEdit}
              />
            )}
          </For>
        </div>
      </Show>
    </section>
  );
}

function GovernCheckRow(props: {
  check: GovernCheck;
  index: number;
  editable: boolean;
  onEdit: (checkId: string, trigger: HTMLElement) => void;
}) {
  const className = `govern-check is-${props.check.result}${checkIntegrity(props.check) ? "" : " is-invalid"}${props.editable ? " is-editable" : " is-read-only"}`;
  const content = (
    <>
      <span class="govern-check-index">{props.index + 1}</span>
      <span class="govern-check-copy">
        <strong>{props.check.criterionText}</strong>
        <Show when={props.check.evidenceNote}>
          <small>{props.check.evidenceNote}</small>
        </Show>
        <Show when={props.check.verifiedBy}>
          <i>Recorded by {props.check.verifiedBy}</i>
        </Show>
      </span>
      <span class={`govern-result is-${props.check.result}`}>
        {RESULT_LABELS[props.check.result]}
      </span>
    </>
  );

  return props.editable ? (
    <button
      type="button"
      class={className}
      aria-label={`Check ${props.index + 1}: ${props.check.criterionText} ${RESULT_LABELS[props.check.result]}`}
      onClick={(event) => props.onEdit(props.check.id, event.currentTarget)}
    >
      {content}
    </button>
  ) : (
    <div class={className}>{content}</div>
  );
}
